import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';

import { fetchTrialStatus } from '@/data/billing';
import { useOrgs } from '@/context/OrgsProvider';

export default function TrialBanner() {
  const { activeOrg } = useOrgs();
  const { data } = useQuery({
    queryKey: ['trial-status', activeOrg?.id],
    queryFn: () => fetchTrialStatus(),
    enabled: !!activeOrg,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (!data?.on_trial || !data.trial_state) return null;

  const expired = data.trial_state === 'expired';
  const urgent = expired || data.trial_state === 'ending_soon';
  const needsBilling = data.has_billing === false;

  const message = expired
    ? data.invoicing_status === 'awaiting_approval'
      ? 'Your free trial has ended. Your billing details are with BitSwan for approval; you will be emailed when invoice billing is switched on. Your servers keep running.'
      : needsBilling || data.invoicing_status === 'terms_pending'
        ? 'Your free trial has ended. Add your billing details and agree to the monthly invoices to continue. Your servers keep running in the meantime.'
        : 'Your free trial has ended. Your servers keep running, but you cannot create new ones.'
    : needsBilling && urgent
      ? `${data.days_left} day${data.days_left === 1 ? '' : 's'} left in your free trial. Add your billing details to keep going after that.`
      : `${data.days_left} day${data.days_left === 1 ? '' : 's'} left in your free trial.`;

  return (
    <div
      className={`flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm ${
        expired
          ? 'border-red-500/30 bg-red-500/10 text-red-700'
          : urgent
            ? 'border-amber-500/30 bg-amber-500/10 text-amber-800'
            : 'border-muted bg-muted/40 text-muted-foreground'
      }`}
      data-testid="trial-banner"
    >
      {urgent ? (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      ) : (
        <Clock className="h-4 w-4 shrink-0" />
      )}
      <span data-testid="trial-banner-text">{message}</span>

      {data.advisor_name ? (
        <span className="text-xs">
          {expired ? 'Talk to' : 'Your advisor is'} {data.advisor_name}.
        </span>
      ) : (
        <Link
          to="/find-an-advisor"
          className="text-xs underline"
          data-testid="trial-banner-advisor-link"
        >
          Find an advisor
        </Link>
      )}

      <Link
        to="/billing"
        className="text-xs underline"
        data-testid="trial-banner-billing-link"
      >
        {needsBilling ? 'Add billing details' : 'Billing details'}
      </Link>
    </div>
  );
}
